import { createTheme } from '@mui/material/styles';

const focusVisibleStyle = (theme) => ({
  '&:focus-visible': {
    outline: `3px solid ${theme.palette.primary.light}`,
    outlineOffset: '2px',
  },
});

const getTheme = (mode) => createTheme({
  palette: {
    mode,
    ...(mode === 'dark' ? {
      primary: {
        main: '#5B8DEF',
        light: '#7BA7F7',
        dark: '#3A6CD4',
        contrastText: '#0F172A',
      },
      secondary: {
        main: '#7C8DB5',
        light: '#A0ADCC',
        dark: '#5A6A8E',
      },
      background: {
        default: '#0B0F19',
        paper: '#111827',
      },
      text: {
        primary: '#E2E8F0',
        secondary: '#94A3B8',
        disabled: '#7E8CA0',
      },
      divider: '#1E293B',
      error: { main: '#EF4444', light: '#F87171' },
      warning: { main: '#F59E0B', light: '#FBBF24' },
      success: { main: '#10B981', light: '#34D399' },
      info: { main: '#3B82F6', light: '#60A5FA' },
      action: {
        hover: 'rgba(91, 141, 239, 0.08)',
        selected: 'rgba(91, 141, 239, 0.16)',
      },
    } : {
      primary: {
        main: '#3A6CD4',
        light: '#5B8DEF',
        dark: '#2554A8',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#5A6A8E',
        light: '#7C8DB5',
        dark: '#3E4C6A',
      },
      background: {
        default: '#F8FAFC',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#0F172A',
        secondary: '#334155',
        disabled: '#6B7A8D',
      },
      divider: '#E2E8F0',
      error: { main: '#EF4444', light: '#F87171' },
      warning: { main: '#F59E0B', light: '#FBBF24' },
      success: { main: '#10B981', light: '#34D399' },
      info: { main: '#3B82F6', light: '#60A5FA' },
    }),
  },
  typography: {
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h1: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.2 },
    h2: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.3 },
    h3: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.3 },
    h4: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.4 },
    h5: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.4 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    button: { fontWeight: 600, textTransform: 'none' },
    caption: { fontSize: '0.8125rem', fontWeight: 500 },
    overline: { fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: "'DM Sans', sans-serif",
        },
        code: {
          fontFamily: "'JetBrains Mono', monospace",
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...focusVisibleStyle(theme),
        }),
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          fontSize: '0.875rem',
        },
        sizeSmall: {
          padding: '4px 12px',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            fontSize: '1rem',
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...focusVisibleStyle(theme),
        }),
        sizeSmall: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minWidth: 44,
            minHeight: 44,
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...focusVisibleStyle(theme),
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...focusVisibleStyle(theme),
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...focusVisibleStyle(theme),
        }),
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12, backgroundImage: 'none' },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.75rem' },
        sizeSmall: { height: 24 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 600,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
        },
      },
    },
  },
});

export default getTheme;
